import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Replace } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { ApiError } from "@/api/client";
import {
  knowledgeBaseApi,
  type KbCategory,
  type KbDocument,
  type KbTag,
} from "@/api/knowledgeBase";
import { useToast } from "@/components/ui/Toast";
import {
  KbDocumentMetadataForm,
  type KbDocumentMetadataState,
} from "./KbDocumentMetadataForm";

// Knowledge Base v1.0 PR 4 — edit dialog. Two operations: update metadata
// (PUT /documents/{id}) and replace file (PUT /documents/{id}/file). Both
// go through the same modal; the user picks via the action row.

interface KbDocumentEditDialogProps {
  document: KbDocument;
  categories: KbCategory[];
  tags: KbTag[];
  onClose: () => void;
  onSaved: () => void;
}

export function KbDocumentEditDialog({
  document,
  categories,
  tags,
  onClose,
  onSaved,
}: KbDocumentEditDialogProps) {
  const toast = useToast();
  const [metadata, setMetadata] = useState<KbDocumentMetadataState>(() => ({
    title: document.title,
    description: document.description ?? "",
    primaryCategoryCode: document.primaryCategoryCode,
    registryAbbrev: document.registryAbbrev ?? "",
    documentVersion: document.documentVersion ?? "",
    effectiveDate: document.effectiveDate
      ? document.effectiveDate.slice(0, 10)
      : "",
    confidentiality: document.confidentiality,
    tagCodes: [...document.tagCodes],
  }));
  const [errors, setErrors] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      knowledgeBaseApi.updateDocumentMetadata(document.id, {
        title: metadata.title.trim(),
        description: metadata.description.trim() || null,
        primaryCategoryCode: metadata.primaryCategoryCode,
        registryAbbrev: metadata.registryAbbrev.trim() || null,
        documentVersion: metadata.documentVersion.trim() || null,
        effectiveDate: metadata.effectiveDate || null,
        confidentiality: metadata.confidentiality,
        tagCodes: metadata.tagCodes,
      }),
    onSuccess: () => {
      toast.success("Metadata updated.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const replaceMutation = useMutation({
    mutationFn: (file: File) => knowledgeBaseApi.replaceDocumentFile(document.id, file),
    onSuccess: () => {
      toast.success("File replaced. Re-indexing will start shortly.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const surface = (err: unknown) => {
    if (err instanceof ApiError && err.errors.length) {
      setErrors(err.errors);
    } else {
      setErrors([err instanceof Error ? err.message : "Save failed."]);
    }
  };

  const submit = () => {
    setErrors([]);
    const issues: string[] = [];
    if (!metadata.title.trim()) issues.push("Title is required.");
    if (!metadata.primaryCategoryCode) issues.push("Primary category is required.");
    if (issues.length) {
      setErrors(issues);
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${document.title}`}
      description="Update the document's metadata, swap the file, or both. File replacement re-runs indexing in the background."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            loading={saveMutation.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-space-4">
        <div className="flex items-center gap-space-3 border border-border-light rounded-md p-space-3 bg-white-warm">
          <div className="flex-1 min-w-0">
            <div className="text-caption text-gray-mid uppercase tracking-wide">
              File
            </div>
            <div className="font-mono text-small text-onyx truncate">
              {document.originalFileName}
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => fileRef.current?.click()}
            loading={replaceMutation.isPending}
          >
            <Replace size={14} aria-hidden /> Replace file
          </Button>
          <input
            ref={fileRef}
            type="file"
            className="sr-only"
            aria-label="Replace file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) replaceMutation.mutate(f);
              e.target.value = "";
            }}
          />
        </div>

        <KbDocumentMetadataForm
          value={metadata}
          onChange={setMetadata}
          categories={categories}
          tags={tags}
          errors={errors}
        />
      </div>
    </Modal>
  );
}
